import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo, { TicketTagsBar } from "../TicketInfo";
import TagSelect from "../TagSelect";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const Ticket = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, navigate]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("ticket", (data) => {
      // Mesma armadilha do MessagesList: o evento "ticket" também vai para as
      // salas de status e de notificação, e a conexão do Socket.IO é uma só
      // para o app inteiro. Sem conferir o id, qualquer mexida em OUTRO
      // atendimento (alguém aceitar, transferir, encerrar) trocava o
      // cabeçalho e os botões desta tela pelos do ticket alheio — e o
      // "delete", que o UpdateTicketService dispara a cada mudança de status,
      // expulsava quem estava lendo a conversa com um "Ticket deleted".
      if (data.action === "update") {
        if (Number(data.ticket?.id) !== Number(ticketId)) return;
        setTicket(data.ticket);
      }

      // `motivo: "status"` é o ticket saindo de uma lista (foi aceito,
      // transferido, encerrado) — continua existindo, e quem está lendo não
      // pode ser expulso da conversa por isso.
      if (data.action === "delete" && data.motivo !== "status") {
        if (Number(data.ticketId) !== Number(ticketId)) return;
        toast.success("Ticket deleted sucessfully.");
        navigate("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, navigate]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <div
      id="drawer-container"
      className="relative flex h-full w-full overflow-hidden"
    >
      <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-background">
        <TicketHeader loading={loading}>
          <TicketInfo
            contact={contact}
            ticket={ticket}
            onClick={handleDrawerOpen}
          />
          <TicketActionButtons ticket={ticket} />
        </TicketHeader>

        {!loading && ticket.id && (
          <TicketTagsBar ticket={ticket}>
            <TagSelect />
          </TicketTagsBar>
        )}
        <ReplyMessageProvider>
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
          ></MessagesList>
          <MessageInput ticketStatus={ticket.status} />
        </ReplyMessageProvider>
      </div>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        ticket={ticket}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
